import axios from "axios";
import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { getApiUrl, API_ENDPOINTS } from "../config/api";

export const AuthContext = createContext({
  isAuthenticated: false,
  loginUser: async () => null,
  logout: () => {},
});

export const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem('accessToken')));
  
  const storeAuthToken = ({ accessToken, expiresAtUtc }) => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      setIsAuthenticated(true);
    }
    if (expiresAtUtc) {
      localStorage.setItem('accessTokenExpiresAtUtc', expiresAtUtc);
    }
  };

  const loginUser = async (userData) => {
    try {
      const url = getApiUrl(API_ENDPOINTS.AUTH_LOGIN);
      const response = await axios.post(url, userData);
      if (response.status === 200 && response?.data?.success) {
        storeAuthToken({
          accessToken: response?.data?.accessToken,
          expiresAtUtc: response?.data?.expiresAtUtc,
        });
        setIsAuthenticated(true);
        // Maintain backward compatibility with existing route guards
        try {
          const payloadPart = (response?.data?.accessToken || '').split('.')[1];
          const payloadJson = payloadPart ? JSON.parse(atob(payloadPart)) : {};
          const username = payloadJson?.name || userData?.username || 'user';
          localStorage.setItem('userDetails', JSON.stringify({ username }));
          // If your API provides roles, set accordingly; default to 'admin' to allow access to admin routes
          localStorage.setItem('role', JSON.stringify('admin'));
        } catch (_) {
          localStorage.setItem('userDetails', JSON.stringify({ username: userData?.username || 'user' }));
          localStorage.setItem('role', JSON.stringify('admin'));
        }
      }
      return response;
    } catch (error) {
      return error;
    }
  };

  const logout = () => {
    // Clear auth/session data from storage
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('accessTokenExpiresAtUtc');
      localStorage.removeItem('userDetails');
      localStorage.removeItem('role');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('accessTokenExpiresAtUtc');
      sessionStorage.removeItem('userDetails');
      sessionStorage.removeItem('role');
      setIsAuthenticated(false);
    } catch (_) {
      // ignore storage errors
    }
  };

  // Keep auth state in sync across tabs/windows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'accessToken') {
        setIsAuthenticated(Boolean(e.newValue));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ loginUser, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
